import { useState, useEffect, useCallback, useRef } from "react";
import axiosInstance from "../../api/axiosInstance";

const TOAST_DURATION = 4000;

const addressApi = {
    list: (vendorProfileId, { q = "", is_active = "" } = {}) => {
        const p = new URLSearchParams();
        if (q) p.set("q", q);
        if (is_active !== "") p.set("is_active", is_active);
        return axiosInstance.get(
            `/api/superadmin/vendors/${vendorProfileId}/user-addresses/?${p}`
        );
    },
    getById: (vendorProfileId, id) =>
        axiosInstance.get(
            `/api/superadmin/vendors/${vendorProfileId}/user-addresses/${id}/`
        ),
    create: (vendorProfileId, body) =>
        axiosInstance.post(
            `/api/superadmin/vendors/${vendorProfileId}/user-addresses/`,
            body
        ),
    patch: (vendorProfileId, id, body) =>
        axiosInstance.patch(
            `/api/superadmin/vendors/${vendorProfileId}/user-addresses/${id}/`,
            body
        ),
    delete: (vendorProfileId, id) =>
        axiosInstance.delete(
            `/api/superadmin/vendors/${vendorProfileId}/user-addresses/${id}/`
        ),
};

const masterApi = {
    countries: (q = "") => {
        const p = new URLSearchParams({ is_active: "true" });
        if (q) p.set("q", q);
        return axiosInstance.get(`/api/superadmin/master/countries/?${p}`);
    },
    states: (countryId, q = "") => {
        const p = new URLSearchParams({ country_id: countryId, is_active: "true" });
        if (q) p.set("q", q);
        return axiosInstance.get(`/api/superadmin/master/states/?${p}`);
    },
    cities: (countryId, stateId, q = "") => {
        const p = new URLSearchParams({
            country_id: countryId,
            state_id: stateId,
            is_active: "true",
        });
        if (q) p.set("q", q);
        return axiosInstance.get(`/api/superadmin/master/cities/?${p}`);
    },
};

let _toastSetter = null;
const toast = {
    _push: (item) => {
        const id = Date.now() + Math.random();
        _toastSetter?.((p) => [...p, { id, ...item }]);
        return id;
    },
    success: (title, msg) => toast._push({ type: "success", title, msg }),
    error: (title, msg) => toast._push({ type: "error", title, msg }),
    info: (title, msg) => toast._push({ type: "info", title, msg }),
    warning: (title, msg) => toast._push({ type: "warning", title, msg }),
    loading: (title) => toast._push({ type: "loading", title, persistent: true }),
    dismiss: (id) => _toastSetter?.((p) => p.filter((t) => t.id !== id)),
};

const TOAST_META = {
    success: { bar: "#16a34a", icon: "✓" },
    error: { bar: "#dc2626", icon: "✕" },
    info: { bar: "#1d4ed8", icon: "i" },
    warning: { bar: "#d97706", icon: "!" },
    loading: { bar: "#94a3b8", icon: null },
};

const ToastItem = ({ t, onDismiss }) => {
    const meta = TOAST_META[t.type];
    const [progress, setProgress] = useState(100);
    const [exiting, setExiting] = useState(false);
    const rafRef = useRef(null);
    const startTs = useRef(Date.now());

    const dismiss = useCallback(() => {
        setExiting(true);
        setTimeout(() => onDismiss(t.id), 280);
    }, [t.id, onDismiss]);

    useEffect(() => {
        if (t.persistent) return;
        const tick = () => {
            const pct = Math.max(
                0,
                100 - ((Date.now() - startTs.current) / TOAST_DURATION) * 100
            );
            setProgress(pct);
            if (pct > 0) rafRef.current = requestAnimationFrame(tick);
            else dismiss();
        };
        rafRef.current = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(rafRef.current);
    }, [t.persistent, dismiss]);

    return (
        <div
            style={{
                position: "relative",
                overflow: "hidden",
                background: "#fff",
                borderRadius: 10,
                borderLeft: `4px solid ${meta.bar}`,
                boxShadow:
                    "0 4px 20px rgba(0,0,0,.10), 0 0 0 1px rgba(0,0,0,.06)",
                minWidth: 300,
                maxWidth: 360,
                animation: exiting
                    ? "ua-alOut .28s ease forwards"
                    : "ua-alIn .28s cubic-bezier(.34,1.56,.64,1) forwards",
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    padding: "13px 14px 14px",
                }}
            >
                <div
                    style={{
                        flexShrink: 0,
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        background: `${meta.bar}18`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        marginTop: 1,
                    }}
                >
                    {t.type === "loading" ? (
                        <svg
                            width="13"
                            height="13"
                            viewBox="0 0 13 13"
                            fill="none"
                            style={{ animation: "ua-spin .85s linear infinite" }}
                        >
                            <circle
                                cx="6.5"
                                cy="6.5"
                                r="5"
                                stroke={meta.bar}
                                strokeWidth="2"
                                strokeDasharray="8 20"
                                strokeLinecap="round"
                            />
                        </svg>
                    ) : (
                        <span
                            style={{
                                fontSize: 11,
                                fontWeight: 800,
                                color: meta.bar,
                                lineHeight: 1,
                            }}
                        >
                            {meta.icon}
                        </span>
                    )}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                        style={{
                            margin: 0,
                            fontSize: 13.5,
                            fontWeight: 650,
                            color: "#0f172a",
                            lineHeight: 1.35,
                        }}
                    >
                        {t.title}
                    </p>
                    {t.msg && (
                        <p
                            style={{
                                margin: "3px 0 0",
                                fontSize: 12.5,
                                color: "#64748b",
                                lineHeight: 1.5,
                            }}
                        >
                            {t.msg}
                        </p>
                    )}
                </div>
                <button
                    onClick={dismiss}
                    style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#cbd5e1",
                        padding: 2,
                        flexShrink: 0,
                        display: "flex",
                        borderRadius: 4,
                    }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = "#94a3b8")}
                    onMouseLeave={(e) => (e.currentTarget.style.color = "#cbd5e1")}
                >
                    <IcoClose />
                </button>
            </div>
            {!t.persistent && (
                <div
                    style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        background: "#f1f5f9",
                    }}
                >
                    <div
                        style={{
                            height: "100%",
                            width: `${progress}%`,
                            background: meta.bar,
                            transition: "width .1s linear",
                            borderRadius: "0 2px 0 0",
                        }}
                    />
                </div>
            )}
        </div>
    );
};

const ToastContainer = () => {
    const [toasts, setToasts] = useState([]);
    _toastSetter = setToasts;
    const dismiss = useCallback(
        (id) => setToasts((p) => p.filter((t) => t.id !== id)),
        []
    );
    if (!toasts.length) return null;
    return (
        <div
            style={{
                position: "fixed",
                bottom: 24,
                right: 24,
                zIndex: 9999,
                display: "flex",
                flexDirection: "column",
                gap: 10,
                alignItems: "flex-end",
            }}
        >
            {toasts.map((t) => (
                <ToastItem key={t.id} t={t} onDismiss={dismiss} />
            ))}
        </div>
    );
};

const IcoClose = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
        <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
);
const IcoLoader = ({ size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
        style={{ animation: "ua-spin 0.8s linear infinite", flexShrink: 0 }}>
        <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
);
const IcoPlus = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
        <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);
const IcoEdit = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </svg>
);
const IcoTrash = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="3 6 5 6 21 6" />
        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
        <path d="M10 11v6M14 11v6" />
        <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </svg>
);
const IcoSearch = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
);
const IcoRefresh = () => (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 4 23 10 17 10" /><polyline points="1 20 1 14 7 14" />
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
);
const IcoEye = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" />
    </svg>
);
const IcoFilter = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
);
const IcoChevronDown = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
        <polyline points="6 9 12 15 18 9" />
    </svg>
);
const IcoMapPin = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
        <circle cx="12" cy="10" r="3" />
    </svg>
);
const IcoPhone = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13a19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 3.6 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 9.91a16 16 0 0 0 6.09 6.09l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
    </svg>
);
const IcoTag = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
);
const IcoBuilding = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M9 21V9" />
    </svg>
);
const IcoAlert = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
);
const IcoStar = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="1">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </svg>
);
const IcoGlobe = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="10" />
        <line x1="2" y1="12" x2="22" y2="12" />
        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
    </svg>
);

const SkeletonBlock = ({ w, h = 14 }) => (
    <div
        style={{
            height: h, width: w, borderRadius: 6,
            background: "linear-gradient(90deg,#f1f5f9 25%,#e2e8f0 50%,#f1f5f9 75%)",
            backgroundSize: "200% 100%",
            animation: "ua-shimmer 1.4s infinite",
        }}
    />
);

const FieldError = ({ msg }) =>
    msg ? (
        <span style={{ fontSize: 11.5, color: "#dc2626", display: "flex", alignItems: "center", gap: 4, marginTop: 4 }}>
            <IcoAlert />{msg}
        </span>
    ) : null;

const StatusBadge = ({ active }) => (
    <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700,
        background: active ? "#dcfce7" : "#f1f5f9",
        color: active ? "#16a34a" : "#94a3b8",
    }}>
        <span style={{ width: 6, height: 6, borderRadius: "50%", background: active ? "#16a34a" : "#94a3b8" }} />
        {active ? "Active" : "Inactive"}
    </span>
);

const DefaultBadge = () => (
    <span style={{
        display: "inline-flex", alignItems: "center", gap: 4,
        padding: "3px 10px", borderRadius: 20, fontSize: 11.5, fontWeight: 700,
        background: "#fef3c7", color: "#d97706",
    }}>
        <IcoStar />Default
    </span>
);

const LABEL_COLORS = {
    Home: ["#dbeafe", "#1d4ed8"],
    Office: ["#dcfce7", "#16a34a"],

    //   Work: ["#dcfce7", "#16a34a"],
    //   "Branch Office": ["#ede9fe", "#6d28d9"],
    //   Warehouse: ["#ffedd5", "#c2410c"],

    Other: ["#f1f5f9", "#475569"],
};
const getLabelColor = (label = "") =>
    LABEL_COLORS[label] || ["#f0f9ff", "#0369a1"];


const SearchableDropdown = ({
    placeholder = "Search…",
    value,
    options,
    loading,
    disabled,
    onChange,
    onSearch,
    error,
    label,
    required,
}) => {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState("");
    const ref = useRef(null);
    const timerRef = useRef(null);

    const selected = options.find((o) => o.id === value);

    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const handleSearch = (val) => {
        setSearch(val);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => onSearch?.(val), 350);
    };

    const filtered = search
        ? options.filter((o) => o.name.toLowerCase().includes(search.toLowerCase()))
        : options;

    const inputStyle = {
        width: "100%", padding: "9px 12px",
        border: `1px solid ${error ? "#fca5a5" : "#e2e8f0"}`,
        borderRadius: 8, fontSize: 13, background: disabled ? "#f8fafc" : "#fff",
        color: disabled ? "#94a3b8" : "#1e293b", outline: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "border-color .15s", boxSizing: "border-box",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        userSelect: "none",
    };

    return (
        <div ref={ref} style={{ position: "relative" }}>
            {label && (
                <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", letterSpacing: ".03em", display: "block", marginBottom: 4 }}>
                    {label}{required && <span style={{ color: "#dc2626" }}> *</span>}
                </label>
            )}
            <div
                style={inputStyle}
                onClick={() => { if (!disabled) setOpen((p) => !p); }}
                onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.borderColor = error ? "#f87171" : "#3b82f6"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = error ? "#fca5a5" : "#e2e8f0"; }}
            >
                <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
                    {selected ? selected.name : <span style={{ color: "#94a3b8" }}>{placeholder}</span>}
                </span>
                {loading ? (
                    <IcoLoader size={14} />
                ) : (
                    <span style={{ color: "#94a3b8", marginLeft: 6, flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }}>
                        <IcoChevronDown />
                    </span>
                )}
            </div>

            {open && (
                <div style={{
                    position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 500,
                    background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0",
                    boxShadow: "0 8px 32px rgba(0,0,0,.12)", overflow: "hidden",
                }}>
                    <div style={{ padding: "8px 8px 4px" }}>
                        <div style={{ position: "relative" }}>
                            <span style={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}>
                                <IcoSearch />
                            </span>
                            <input
                                autoFocus
                                value={search}
                                onChange={(e) => handleSearch(e.target.value)}
                                placeholder="Search…"
                                style={{
                                    width: "100%", padding: "7px 10px 7px 30px", border: "1px solid #e2e8f0",
                                    borderRadius: 7, fontSize: 12.5, outline: "none", boxSizing: "border-box",
                                    background: "#f8fafc", color: "#1e293b",
                                }}
                                onClick={(e) => e.stopPropagation()}
                            />
                        </div>
                    </div>
                    <div style={{ maxHeight: 220, overflowY: "auto" }}>
                        {loading ? (
                            <div style={{ padding: "12px 14px", color: "#94a3b8", fontSize: 12.5 }}>Loading…</div>
                        ) : filtered.length === 0 ? (
                            <div style={{ padding: "12px 14px", color: "#94a3b8", fontSize: 12.5 }}>No results found</div>
                        ) : (
                            filtered.map((opt) => (
                                <div key={opt.id}
                                    onClick={() => { onChange(opt.id); setOpen(false); setSearch(""); }}
                                    style={{
                                        padding: "9px 14px", fontSize: 13, cursor: "pointer",
                                        background: opt.id === value ? "#eff6ff" : "transparent",
                                        color: opt.id === value ? "#1d4ed8" : "#1e293b",
                                        fontWeight: opt.id === value ? 600 : 400,
                                        transition: "background .1s",
                                    }}
                                    onMouseEnter={(e) => { if (opt.id !== value) e.currentTarget.style.background = "#f8fafc"; }}
                                    onMouseLeave={(e) => { if (opt.id !== value) e.currentTarget.style.background = "transparent"; }}
                                >
                                    {opt.name}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}
            <FieldError msg={error} />
        </div>
    );
};

const AddressCard = ({ address, onView, onEdit, onDelete }) => {
    const [bg, fg] = getLabelColor(address.label);
    const fullAddress = [
        address.address_line1,
        address.address_line2,
        address.landmark && `Near ${address.landmark}`,
        address.city_name,
        address.state_name,
        address.country_name,
        address.pincode,
    ]
        .filter(Boolean)
        .join(", ");

    return (
        <div
            className="ua-card"
            style={{
                background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12,
                overflow: "hidden", boxShadow: "0 1px 4px rgba(0,0,0,.04)",
                transition: "box-shadow .2s, transform .2s", display: "flex", flexDirection: "column",
            }}
        >
            <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid #f8fafc" }}>
                <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", flex: 1, minWidth: 0 }}>
                        <span style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                            background: bg, color: fg,
                        }}>
                            <IcoTag />{address.label || "—"}
                        </span>
                        {address.is_default && <DefaultBadge />}
                    </div>
                    <StatusBadge active={address.is_active} />
                </div>

                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 700, color: "#0f172a" }}>
                    {address.name}
                </p>

                <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 5 }}>
                    <span style={{ color: "#94a3b8", flexShrink: 0 }}><IcoPhone /></span>
                    <span style={{ fontSize: 12.5, color: "#64748b" }}>{address.mobile}</span>
                </div>
            </div>

            <div style={{ padding: "10px 16px 12px", flex: 1 }}>
                <div style={{ display: "flex", gap: 7, alignItems: "flex-start" }}>
                    <span style={{ color: "#94a3b8", flexShrink: 0, marginTop: 1 }}><IcoMapPin /></span>
                    <p style={{ margin: 0, fontSize: 12.5, color: "#475569", lineHeight: 1.65, wordBreak: "break-word" }}>
                        {fullAddress}
                    </p>
                </div>

                <div style={{ display: "flex", gap: 7, alignItems: "center", marginTop: 8 }}>
                    <span style={{ color: "#94a3b8", flexShrink: 0 }}><IcoGlobe /></span>
                    <span style={{ fontSize: 12, color: "#94a3b8", fontWeight: 600 }}>
                        {address.country_iso2} · {address.state_code}
                    </span>
                    <span style={{
                        marginLeft: "auto", fontSize: 11.5, fontWeight: 700,
                        background: "#f1f5f9", color: "#475569",
                        padding: "2px 8px", borderRadius: 6,
                    }}>
                        {address.pincode}
                    </span>
                </div>
            </div>

            <div style={{ display: "flex", borderTop: "1px solid #f1f5f9" }}>
                {[
                    { icon: <IcoEye />, label: "View", color: "#6366f1", bg: "#f5f3ff", fn: () => onView(address) },
                    { icon: <IcoEdit />, label: "Edit", color: "#d97706", bg: "#fffbeb", fn: () => onEdit(address) },
                    { icon: <IcoTrash />, label: "Delete", color: "#dc2626", bg: "#fff1f2", fn: () => onDelete(address) },
                ].map(({ icon, label, color, bg: hbg, fn }, i) => (
                    <button
                        key={label}
                        onClick={fn}
                        style={{
                            flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                            gap: 5, padding: "9px 0", background: "#fff", border: "none",
                            borderRight: i < 2 ? "1px solid #f1f5f9" : "none",
                            cursor: "pointer", fontSize: 12.5, fontWeight: 600, color: "#64748b",
                            transition: "background .15s, color .15s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = hbg; e.currentTarget.style.color = color; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = "#64748b"; }}
                    >
                        {icon}{label}
                    </button>
                ))}
            </div>
        </div>
    );
};

const AddressSkeleton = () => (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14 }}>
        {[1, 2, 3].map((i) => (
            <div key={i} style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
                <div style={{ padding: "14px 16px 10px", borderBottom: "1px solid #f8fafc" }}>
                    <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                        <SkeletonBlock w={60} h={22} />
                        <SkeletonBlock w={70} h={22} />
                    </div>
                    <SkeletonBlock w="50%" h={15} />
                    <div style={{ marginTop: 6 }}><SkeletonBlock w="35%" h={13} /></div>
                </div>
                <div style={{ padding: "10px 16px 12px" }}>
                    <SkeletonBlock w="90%" h={13} />
                    <div style={{ marginTop: 5 }}><SkeletonBlock w="60%" h={12} /></div>
                    <div style={{ marginTop: 8 }}><SkeletonBlock w="75%" h={13} /></div>
                </div>
                <div style={{ height: 38, background: "#f8fafc", borderTop: "1px solid #f1f5f9" }} />
            </div>
        ))}
    </div>
);

const ConfirmDeleteModal = ({ address, onConfirm, onCancel, loading }) => (
    <div
        style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2000, padding: 20 }}
        onClick={onCancel}
    >
        <div
            style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 420, padding: 28, boxShadow: "0 24px 64px rgba(0,0,0,.20)", animation: "ua-fadeUp .2s ease" }}
            onClick={(e) => e.stopPropagation()}
        >
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 14, textAlign: "center" }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#dc2626" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                </div>
                <div>
                    <h3 style={{ margin: "0 0 6px", fontSize: 17, fontWeight: 700, color: "#0f172a" }}>Delete Address?</h3>
                    <p style={{ margin: 0, fontSize: 13.5, color: "#64748b", lineHeight: 1.6 }}>
                        The <strong style={{ color: "#0f172a" }}>{address?.label || "address"}</strong> for{" "}
                        <strong style={{ color: "#0f172a" }}>{address?.name}</strong> will be permanently removed. This action cannot be undone.
                    </p>
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 4, width: "100%" }}>
                    <button
                        onClick={onCancel}
                        disabled={loading}
                        style={{ flex: 1, padding: "10px 0", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer", background: "#fff", color: "#374151" }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={loading}
                        style={{ flex: 1, padding: "10px 0", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", background: loading ? "#fca5a5" : "#dc2626", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
                    >
                        {loading ? <><IcoLoader size={14} /> Deleting…</> : <><IcoTrash /> Delete</>}
                    </button>
                </div>
            </div>
        </div>
    </div>
);

const AddressDetailModal = ({ address, onClose, onEdit }) => {
    useEffect(() => {
        document.body.style.overflow = "hidden";
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
    }, [onClose]);

    const Row = ({ icon, label, value }) => (
        <div style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "8px 0", borderBottom: "1px solid #f8fafc" }}>
            <span style={{ color: "#94a3b8", flexShrink: 0, marginTop: 1 }}>{icon}</span>
            <span style={{ fontSize: 12.5, color: "#94a3b8", fontWeight: 600, minWidth: 140, flexShrink: 0 }}>{label}</span>
            <span style={{ fontSize: 13, color: "#1e293b", fontWeight: 500, flex: 1, wordBreak: "break-word" }}>
                {value ?? <span style={{ color: "#cbd5e1" }}>—</span>}
            </span>
        </div>
    );

    return (
        <div
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
            onClick={onClose}
        >
            <div
                style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 560, boxShadow: "0 24px 64px rgba(0,0,0,.20)", animation: "ua-fadeUp .2s ease", maxHeight: "90vh", display: "flex", flexDirection: "column" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ padding: "18px 24px", borderBottom: "1px solid #f1f5f9", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexShrink: 0 }}>
                    <div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
                            {(() => {
                                const [bg, fg] = getLabelColor(address.label);
                                return (
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 10px", borderRadius: 20, fontSize: 12, fontWeight: 700, background: bg, color: fg }}>
                                        <IcoTag />{address.label || "—"}
                                    </span>
                                );
                            })()}
                            {address.is_default && <DefaultBadge />}
                            <StatusBadge active={address.is_active} />
                        </div>
                        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#0f172a" }}>{address.name}</h2>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 6, borderRadius: 6, flexShrink: 0 }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "#64748b")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
                    >
                        <IcoClose />
                    </button>
                </div>

                <div style={{ overflowY: "auto", padding: "18px 24px", flex: 1 }}>
                    <div style={{ background: "#f8fafc", borderRadius: 10, padding: "4px 12px", marginBottom: 14 }}>
                        <Row icon={<IcoBuilding />} label="Contact Name" value={address.name} />
                        <Row icon={<IcoPhone />} label="Mobile" value={address.mobile} />
                        <Row icon={<IcoTag />} label="Label" value={address.label} />
                    </div>
                    <div style={{ background: "#f8fafc", borderRadius: 10, padding: "4px 12px", marginBottom: 14 }}>
                        <Row icon={<IcoMapPin />} label="Address Line 1" value={address.address_line1} />
                        <Row icon={<IcoMapPin />} label="Address Line 2" value={address.address_line2 || "—"} />
                        <Row icon={<IcoMapPin />} label="Landmark" value={address.landmark || "—"} />
                        <Row icon={<IcoMapPin />} label="Pincode" value={address.pincode} />
                    </div>
                    <div style={{ background: "#f8fafc", borderRadius: 10, padding: "4px 12px" }}>
                        <Row icon={<IcoGlobe />} label="City" value={address.city_name} />
                        <Row icon={<IcoGlobe />} label="State" value={`${address.state_name} (${address.state_code})`} />
                        <Row icon={<IcoGlobe />} label="Country" value={`${address.country_name} (${address.country_iso2})`} />
                        <Row icon={<IcoBuilding />} label="Created At" value={address.created_at} />
                        <Row icon={<IcoBuilding />} label="Updated At" value={address.updated_at} />
                    </div>
                </div>

                <div style={{ padding: "14px 24px", borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end", gap: 10, flexShrink: 0 }}>
                    <button
                        onClick={onClose}
                        style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: "pointer", background: "#fff", color: "#374151" }}
                    >
                        Close
                    </button>
                    <button
                        onClick={() => { onClose(); onEdit(address); }}
                        style={{ padding: "9px 20px", border: "none", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: "pointer", background: "#f59e0b", color: "#fff", display: "flex", alignItems: "center", gap: 6 }}
                    >
                        <IcoEdit /> Edit Address
                    </button>
                </div>
            </div>
        </div>
    );
};

const DEFAULT_FORM = {
    name: "", mobile: "", label: "", address_line1: "", address_line2: "",
    landmark: "", pincode: "", is_default: false, is_active: true,
    country_id: null, state_id: null, city_id: null,
};

const AddressFormModal = ({ mode, initialData, vendorProfileId, onClose, onSuccess }) => {
    const isEdit = mode === "edit";

    const [form, setForm] = useState(() => {
        if (isEdit && initialData) {
            return {
                name: initialData.name ?? "",
                mobile: initialData.mobile ?? "",
                label: initialData.label ?? "",
                address_line1: initialData.address_line1 ?? "",
                address_line2: initialData.address_line2 ?? "",
                landmark: initialData.landmark ?? "",
                pincode: initialData.pincode ?? "",
                is_default: initialData.is_default ?? false,
                is_active: initialData.is_active ?? true,
                country_id: initialData.country ?? null,
                state_id: initialData.state ?? null,
                city_id: initialData.city ?? null,
            };
        }
        return { ...DEFAULT_FORM };
    });

    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [loadingStates, setLoadingStates] = useState(false);
    const [loadingCities, setLoadingCities] = useState(false);

    useEffect(() => {
        setLoadingCountries(true);
        masterApi.countries()
            .then(({ data }) => setCountries(data?.results ?? []))
            .catch(() => toast.error("Error", "Failed to load countries."))
            .finally(() => setLoadingCountries(false));
    }, []);

    useEffect(() => {
        if (!form.country_id) { setStates([]); setCities([]); return; }
        setLoadingStates(true);
        masterApi.states(form.country_id)
            .then(({ data }) => setStates(data?.results ?? []))
            .catch(() => toast.error("Error", "Failed to load states."))
            .finally(() => setLoadingStates(false));
    }, [form.country_id]);

    useEffect(() => {
        if (!form.country_id || !form.state_id) { setCities([]); return; }
        setLoadingCities(true);
        masterApi.cities(form.country_id, form.state_id)
            .then(({ data }) => setCities(data?.results ?? []))
            .catch(() => toast.error("Error", "Failed to load cities."))
            .finally(() => setLoadingCities(false));
    }, [form.country_id, form.state_id]);

    useEffect(() => {
        document.body.style.overflow = "hidden";
        const onKey = (e) => { if (e.key === "Escape") onClose(); };
        document.addEventListener("keydown", onKey);
        return () => { document.body.style.overflow = ""; document.removeEventListener("keydown", onKey); };
    }, [onClose]);

    const setField = (name, value) => {
        setForm((p) => {
            const next = { ...p, [name]: value };
            if (name === "country_id") { next.state_id = null; next.city_id = null; }
            if (name === "state_id") { next.city_id = null; }
            return next;
        });
        if (errors[name]) setErrors((p) => ({ ...p, [name]: null }));
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = "Contact name is required.";
        if (!form.mobile.trim()) e.mobile = "Mobile number is required.";
        else if (!/^\d{10}$/.test(form.mobile.trim())) e.mobile = "Enter a valid 10-digit mobile number.";
        if (!form.label.trim()) e.label = "Label is required.";
        if (!form.address_line1.trim()) e.address_line1 = "Address Line 1 is required.";
        if (!form.pincode.trim()) e.pincode = "Pincode is required.";
        else if (!/^\d{4,10}$/.test(form.pincode.trim())) e.pincode = "Enter a valid pincode.";
        if (!form.country_id) e.country_id = "Please select a country.";
        if (!form.state_id) e.state_id = "Please select a state.";
        if (!form.city_id) e.city_id = "Please select a city.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        setSaving(true);
        const loadId = toast.loading(isEdit ? "Updating address…" : "Creating address…");

        try {
            let res;
            if (isEdit) {
                const payload = {
                    name: form.name.trim(),
                    mobile: form.mobile.trim(),
                    label: form.label.trim(),
                    address_line1: form.address_line1.trim(),
                    address_line2: form.address_line2.trim(),
                    landmark: form.landmark.trim(),
                    pincode: form.pincode.trim(),
                    is_default: form.is_default,
                    is_active: form.is_active,
                    country_id: form.country_id,
                    state_id: form.state_id,
                    city_id: form.city_id,
                };
                res = await addressApi.patch(vendorProfileId, initialData.id, payload);
            } else {
                const payload = {
                    name: form.name.trim(),
                    mobile: form.mobile.trim(),
                    label: form.label.trim(),
                    address_line1: form.address_line1.trim(),
                    address_line2: form.address_line2.trim(),
                    landmark: form.landmark.trim(),
                    pincode: form.pincode.trim(),
                    is_default: form.is_default,
                    is_active: form.is_active,
                    country_id: form.country_id,
                    state_id: form.state_id,
                    city_id: form.city_id,
                };
                res = await addressApi.create(vendorProfileId, payload);
            }

            const { data } = res;

            if (data?.status >= 400) {
                const detail = data?.data?.detail || data?.data?.message || `Failed`;
                const fieldErrs = {};
                Object.entries(data?.data || {}).forEach(([k, v]) => {
                    if (!["detail", "message", "status"].includes(k))
                        fieldErrs[k] = Array.isArray(v) ? v[0] : String(v);
                });
                if (Object.keys(fieldErrs).length) setErrors(fieldErrs);
                throw new Error(detail);
            }

            toast.dismiss(loadId);
            toast.success(
                isEdit ? "Address Updated" : "Address Added",
                isEdit ? "Address details updated successfully." : "New address has been added successfully."
            );
            onSuccess(data?.data ?? data);
            onClose();
        } catch (err) {
            toast.dismiss(loadId);
            toast.error(
                isEdit ? "Update Failed" : "Create Failed",
                err.message || "An unexpected error occurred. Please try again."
            );
        } finally {
            setSaving(false);
        }
    };

    const inputStyle = (field) => ({
        width: "100%", padding: "9px 12px",
        border: `1px solid ${errors[field] ? "#fca5a5" : "#e2e8f0"}`,
        borderRadius: 8, fontSize: 13, background: "#fff", color: "#1e293b",
        outline: "none", transition: "border-color .15s", boxSizing: "border-box",
    });

    const Label = ({ children, required }) => (
        <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", letterSpacing: ".03em", display: "block", marginBottom: 4 }}>
            {children}{required && <span style={{ color: "#dc2626" }}> *</span>}
        </label>
    );

    return (
        <div
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,.55)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "16px" }}
            onClick={onClose}
        >
            <div
                style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 600, boxShadow: "0 24px 64px rgba(0,0,0,.20)", animation: "ua-fadeUp .2s ease", display: "flex", flexDirection: "column", maxHeight: "92vh" }}
                onClick={(e) => e.stopPropagation()}
            >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "18px 24px", borderBottom: "1px solid #f1f5f9", flexShrink: 0 }}>
                    <div>
                        <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
                            {isEdit ? "Edit Address" : "Add New Address"}
                        </h2>
                        <p style={{ margin: "2px 0 0", fontSize: 12, color: "#94a3b8" }}>
                            {isEdit ? `Editing address for ${initialData?.name}` : "Fill in the details to add a new address"}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 6, borderRadius: 6 }}
                        onMouseEnter={(e) => (e.currentTarget.style.color = "#64748b")}
                        onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
                    >
                        <IcoClose />
                    </button>
                </div>

                <div style={{ overflowY: "auto", padding: "20px 24px", flex: 1, display: "flex", flexDirection: "column", gap: 14 }}>

                    <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px" }}>
                        <p style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".06em" }}>
                            Contact Information
                        </p>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                            <div>
                                <Label required>Contact Name</Label>
                                <input
                                    value={form.name}
                                    onChange={(e) => setField("name", e.target.value)}
                                    placeholder="e.g. John Doe"
                                    style={inputStyle("name")}
                                    onFocus={(e) => { e.target.style.borderColor = errors.name ? "#f87171" : "#3b82f6"; }}
                                    onBlur={(e) => { e.target.style.borderColor = errors.name ? "#fca5a5" : "#e2e8f0"; }}
                                />
                                <FieldError msg={errors.name} />
                            </div>
                            <div>
                                <Label required>Mobile Number</Label>
                                <input
                                    value={form.mobile}
                                    onChange={(e) => setField("mobile", e.target.value)}
                                    placeholder="e.g. 9876543210"
                                    maxLength={10}
                                    style={inputStyle("mobile")}
                                    onFocus={(e) => { e.target.style.borderColor = errors.mobile ? "#f87171" : "#3b82f6"; }}
                                    onBlur={(e) => { e.target.style.borderColor = errors.mobile ? "#fca5a5" : "#e2e8f0"; }}
                                />
                                <FieldError msg={errors.mobile} />
                            </div>
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <Label required>Label</Label>
                            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {["Home", "Office",  "Other"].map((l) => {
                                    const [bg, fg] = getLabelColor(l);
                                    const isSelected = form.label === l;
                                    return (
                                        <button
                                            key={l}
                                            type="button"
                                            onClick={() => setField("label", l)}
                                            style={{
                                                padding: "5px 14px", borderRadius: 20, fontSize: 12.5, fontWeight: 600,
                                                border: isSelected ? `2px solid ${fg}` : "1.5px solid #e2e8f0",
                                                background: isSelected ? bg : "#fff",
                                                color: isSelected ? fg : "#64748b",
                                                cursor: "pointer", transition: "all .15s",
                                            }}
                                        >
                                            {l}
                                        </button>
                                    );
                                })}
                            </div>
                            {errors.label && <FieldError msg={errors.label} />}
                        </div>
                    </div>

                    <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px" }}>
                        <p style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".06em" }}>
                            Address Details
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <div>
                                <Label required>Address Line 1</Label>
                                <input
                                    value={form.address_line1}
                                    onChange={(e) => setField("address_line1", e.target.value)}
                                    placeholder="Street address, building number…"
                                    style={inputStyle("address_line1")}
                                    onFocus={(e) => { e.target.style.borderColor = errors.address_line1 ? "#f87171" : "#3b82f6"; }}
                                    onBlur={(e) => { e.target.style.borderColor = errors.address_line1 ? "#fca5a5" : "#e2e8f0"; }}
                                />
                                <FieldError msg={errors.address_line1} />
                            </div>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                                <div>
                                    <Label>Address Line 2</Label>
                                    <input
                                        value={form.address_line2}
                                        onChange={(e) => setField("address_line2", e.target.value)}
                                        placeholder="Apartment, suite, floor…"
                                        style={inputStyle("address_line2")}
                                        onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; }}
                                        onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; }}
                                    />
                                </div>
                                <div>
                                    <Label>Landmark</Label>
                                    <input
                                        value={form.landmark}
                                        onChange={(e) => setField("landmark", e.target.value)}
                                        placeholder="Near Central Park…"
                                        style={inputStyle("landmark")}
                                        onFocus={(e) => { e.target.style.borderColor = "#3b82f6"; }}
                                        onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; }}
                                    />
                                </div>
                            </div>
                            <div>
                                <Label required>Pincode</Label>
                                <input
                                    value={form.pincode}
                                    onChange={(e) => setField("pincode", e.target.value)}
                                    placeholder="e.g. 110001"
                                    maxLength={10}
                                    style={inputStyle("pincode")}
                                    onFocus={(e) => { e.target.style.borderColor = errors.pincode ? "#f87171" : "#3b82f6"; }}
                                    onBlur={(e) => { e.target.style.borderColor = errors.pincode ? "#fca5a5" : "#e2e8f0"; }}
                                />
                                <FieldError msg={errors.pincode} />
                            </div>
                        </div>
                    </div>

                    <div style={{ background: "#f8fafc", borderRadius: 10, padding: "12px 14px" }}>
                        <p style={{ margin: "0 0 10px", fontSize: 12, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: ".06em" }}>
                            Location
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                            <SearchableDropdown
                                label="Country"
                                required
                                placeholder="Select country…"
                                value={form.country_id}
                                options={countries}
                                loading={loadingCountries}
                                disabled={loadingCountries}
                                onChange={(id) => setField("country_id", id)}
                                error={errors.country_id}
                            />
                            <SearchableDropdown
                                label="State"
                                required
                                placeholder={form.country_id ? "Select state…" : "Select country first"}
                                value={form.state_id}
                                options={states}
                                loading={loadingStates}
                                disabled={!form.country_id || loadingStates}
                                onChange={(id) => setField("state_id", id)}
                                error={errors.state_id}
                            />
                            <SearchableDropdown
                                label="City"
                                required
                                placeholder={form.state_id ? "Select city…" : "Select state first"}
                                value={form.city_id}
                                options={cities}
                                loading={loadingCities}
                                disabled={!form.state_id || loadingCities}
                                onChange={(id) => setField("city_id", id)}
                                error={errors.city_id}
                            />
                        </div>
                    </div>

                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <div
                            style={{ flex: 1, minWidth: 200, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: form.is_default ? "#fef9c3" : "#f8fafc", borderRadius: 9, border: `1px solid ${form.is_default ? "#fde047" : "#e2e8f0"}`, transition: "all .2s" }}
                        >
                            <span style={{ fontSize: 13, fontWeight: 600, color: form.is_default ? "#854d0e" : "#64748b", display: "flex", alignItems: "center", gap: 7 }}>
                                <IcoStar />{form.is_default ? "Set as Default" : "Make Default"}
                            </span>
                            <button
                                type="button"
                                onClick={() => setField("is_default", !form.is_default)}
                                style={{ width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer", background: form.is_default ? "#d97706" : "#e2e8f0", position: "relative", transition: "background .2s", flexShrink: 0 }}
                            >
                                <span style={{ position: "absolute", top: 2, left: form.is_default ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.15)", transition: "left .2s" }} />
                            </button>
                        </div>

                        <div
                            style={{ flex: 1, minWidth: 200, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: form.is_active ? "#f0fdf4" : "#f8fafc", borderRadius: 9, border: `1px solid ${form.is_active ? "#bbf7d0" : "#e2e8f0"}`, transition: "all .2s" }}
                        >
                            <span style={{ fontSize: 13, fontWeight: 600, color: form.is_active ? "#166534" : "#64748b", display: "flex", alignItems: "center", gap: 7 }}>
                                <IcoBuilding />{form.is_active ? "Address Active" : "Address Inactive"}
                            </span>
                            <button
                                type="button"
                                onClick={() => setField("is_active", !form.is_active)}
                                style={{ width: 44, height: 24, borderRadius: 12, border: "none", cursor: "pointer", background: form.is_active ? "#16a34a" : "#e2e8f0", position: "relative", transition: "background .2s", flexShrink: 0 }}
                            >
                                <span style={{ position: "absolute", top: 2, left: form.is_active ? 22 : 2, width: 20, height: 20, borderRadius: "50%", background: "#fff", boxShadow: "0 1px 3px rgba(0,0,0,.15)", transition: "left .2s" }} />
                            </button>
                        </div>
                    </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, padding: "16px 24px", borderTop: "1px solid #f1f5f9", flexShrink: 0 }}>
                    <button
                        onClick={onClose}
                        disabled={saving}
                        style={{ padding: "9px 20px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: "pointer", background: "#fff", color: "#374151" }}
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSubmit}
                        disabled={saving}
                        style={{ padding: "9px 22px", border: "none", borderRadius: 8, fontSize: 13.5, fontWeight: 600, cursor: saving ? "not-allowed" : "pointer", background: saving ? "#93c5fd" : isEdit ? "#f59e0b" : "#3b82f6", color: "#fff", display: "flex", alignItems: "center", gap: 7 }}
                    >
                        {saving ? <><IcoLoader size={14} /> Saving…</> : isEdit ? <><IcoEdit /> Update Address</> : <><IcoPlus /> Add Address</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

const UserAddress = ({ vendorProfileId }) => {
    const [addresses, setAddresses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [activeFilter, setActiveFilter] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [formMode, setFormMode] = useState("create");
    const [selectedAddress, setSelectedAddress] = useState(null);
    const [detailAddress, setDetailAddress] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
      const timer = setTimeout(() => setDebouncedSearch(search), 380);
      return () => clearTimeout(timer);
    }, [search]);

    const fetchAddresses = useCallback(
        async (q = debouncedSearch, filter = activeFilter) => {
            setLoading(true);
            try {
                const { data } = await addressApi.list(vendorProfileId, { q, is_active: filter });

                if (data?.status === 403) {
                    toast.error("Access Denied", "You don't have permission to view addresses.");
                    return;
                }

                const list = data?.results ?? data?.data ?? (Array.isArray(data) ? data : []);
                setAddresses(list);
            } catch (err) {
                const isNetwork = err?.name === "TypeError";
                toast.error(
                    isNetwork ? "Network Error" : "Failed to Load",
                    isNetwork ? "Unable to reach the server. Check your connection." : err.message || "Could not fetch addresses. Please try again."
                );
                setAddresses([]);
            } finally {
                setLoading(false);
            }
        },
        [vendorProfileId, debouncedSearch, activeFilter]
    );

    useEffect(() => { fetchAddresses(); }, [fetchAddresses]);

    const handleSearchChange = (val) => {
        setSearch(val);
    };

    const handleFilterChange = (val) => {
        setDebouncedSearch(search);
        setActiveFilter(val);
    };

    const handleDelete = async () => {
        if (!deleteTarget) return;
        setDeleting(true);
        const loadId = toast.loading("Deleting address…");
        try {
            const { data } = await addressApi.delete(vendorProfileId, deleteTarget.id);
            if (data?.status >= 400) {
                throw new Error(data?.data?.detail || data?.data?.message || "Delete failed");
            }
            toast.dismiss(loadId);
            toast.success("Address Deleted", `${deleteTarget.label || "Address"} for ${deleteTarget.name} has been removed.`);
            setAddresses((p) => p.filter((a) => a.id !== deleteTarget.id));
            setDeleteTarget(null);
        } catch (err) {
            toast.dismiss(loadId);
            toast.error("Delete Failed", err.message || "Could not delete address. Please try again.");
        } finally {
            setDeleting(false);
        }
    };

    const handleFormSuccess = (saved) => {
        if (formMode === "edit") {
            setAddresses((p) => p.map((a) => (a.id === saved.id ? saved : a)));
        } else {
            setAddresses((p) => [saved, ...p]);
        }
    };

    const total = addresses.length;
    const active = addresses.filter((a) => a.is_active).length;
    const defaultCount = addresses.filter((a) => a.is_default).length;

    return (
        <div style={{ fontFamily: "'Inter', -apple-system, sans-serif", color: "#1e293b" }}>
            <style>{`
        @keyframes ua-shimmer  { 0%{background-position:200% 0}100%{background-position:-200% 0} }
        @keyframes ua-fadeUp   { from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:none} }
        @keyframes ua-alIn     { from{opacity:0;transform:translateX(24px) scale(.97)}to{opacity:1;transform:none} }
        @keyframes ua-alOut    { from{opacity:1}to{opacity:0;transform:translateX(28px) scale(.95)} }
        @keyframes ua-spin     { from{transform:rotate(0deg)}to{transform:rotate(360deg)} }
        .ua-card:hover  { box-shadow:0 6px 20px rgba(0,0,0,.09) !important; transform:translateY(-1px); }
        * { box-sizing:border-box; }
        @media (max-width: 640px) {
          .ua-stats { flex-wrap: wrap !important; }
          .ua-toolbar { flex-direction: column !important; }
          .ua-toolbar > * { width: 100% !important; }
          .ua-header { flex-direction: column !important; align-items: flex-start !important; }
          .ua-header-actions { width: 100%; }
          .ua-header-actions button { flex: 1; justify-content: center; }
        }
      `}</style>

            <ToastContainer />

            <div className="ua-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
                <div>
                    <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#0f172a" }}>User Addresses</h2>
                    <p style={{ margin: "2px 0 0", fontSize: 12.5, color: "#64748b" }}>Manage shipping and billing addresses</p>
                </div>
                <div className="ua-header-actions" style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <button
                        onClick={() => fetchAddresses(search, activeFilter)}
                        disabled={loading}
                        style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#fff", color: "#374151", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: loading ? "not-allowed" : "pointer", boxShadow: "0 1px 3px rgba(0,0,0,.05)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#f1f5f9")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
                    >
                        {loading ? <IcoLoader size={15} /> : <IcoRefresh />} Refresh
                    </button>
                    <button
                        onClick={() => { setFormMode("create"); setSelectedAddress(null); setShowForm(true); }}
                        style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 16px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", transition: "opacity .15s" }}
                        onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
                        onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
                    >
                        <IcoPlus /> Add Address
                    </button>
                </div>
            </div>

            {!loading && (
                <div className="ua-stats" style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", animation: "ua-fadeUp .2s ease" }}>
                    {[
                        { label: "Total Addresses", value: total, bg: "#f8fafc", border: "#e2e8f0", color: "#0f172a" },
                        { label: "Active", value: active, bg: "#f0fdf4", border: "#bbf7d0", color: "#16a34a" },
                        { label: "Inactive", value: total - active, bg: "#f8fafc", border: "#e2e8f0", color: "#94a3b8" },
                        { label: "Default Set", value: defaultCount, bg: "#fef9c3", border: "#fde047", color: "#854d0e" },
                    ].map(({ label, value, bg, border, color }) => (
                        <div key={label} style={{ padding: "10px 18px", background: bg, border: `1px solid ${border}`, borderRadius: 10 }}>
                            <span style={{ fontSize: 11, fontWeight: 600, color: "#94a3b8", textTransform: "uppercase", letterSpacing: ".06em", display: "block" }}>{label}</span>
                            <span style={{ fontSize: 20, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</span>
                        </div>
                    ))}
                </div>
            )}

            <div className="ua-toolbar" style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                <div style={{ position: "relative", flex: 1, minWidth: 220 }}>
                    <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}>
                        <IcoSearch />
                    </span>
                    <input
                        value={search}
                        onChange={(e) => handleSearchChange(e.target.value)}
                        placeholder="Search by name, city, pincode, label…"
                        style={{ width: "100%", padding: "9px 12px 9px 34px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, background: "#fff", color: "#1e293b", outline: "none", boxSizing: "border-box" }}
                        onFocus={(e) => (e.target.style.borderColor = "#3b82f6")}
                        onBlur={(e) => (e.target.style.borderColor = "#e2e8f0")}
                    />
                    {search && (
                        <button
                            onClick={() => handleSearchChange("")}
                            style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "#94a3b8", display: "flex", padding: 2 }}
                        >
                            <IcoClose />
                        </button>
                    )}
                </div>

                <div style={{ position: "relative" }}>
                    <span style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}>
                        <IcoFilter />
                    </span>
                    <select
                        value={activeFilter}
                        onChange={(e) => handleFilterChange(e.target.value)}
                        style={{ padding: "9px 32px 9px 32px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, background: "#fff", color: "#1e293b", outline: "none", appearance: "none", cursor: "pointer", minWidth: 140 }}
                    >
                        <option value="">All Status</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select>
                    <span style={{ position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", pointerEvents: "none" }}>
                        <IcoChevronDown />
                    </span>
                </div>
            </div>

            {loading ? (
                <AddressSkeleton />
            ) : addresses.length === 0 ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "60px 20px", background: "#fff", borderRadius: 12, border: "1px dashed #e2e8f0", gap: 12, textAlign: "center", animation: "ua-fadeUp .2s ease" }}>
                    <div style={{ width: 64, height: 64, borderRadius: "50%", background: "linear-gradient(135deg,#f1f5f9,#e2e8f0)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <IcoMapPin />
                    </div>
                    <div>
                        <p style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 700, color: "#374151" }}>
                            {search || activeFilter ? "No addresses match your filters" : "No Addresses Yet"}
                        </p>
                        <p style={{ margin: 0, fontSize: 13.5, color: "#94a3b8" }}>
                            {search || activeFilter
                                ? "Try adjusting your search or filter criteria."
                                : "Add an address to manage shipping and billing locations."}
                        </p>
                    </div>
                    {!search && !activeFilter && (
                        <button
                            onClick={() => { setFormMode("create"); setSelectedAddress(null); setShowForm(true); }}
                            style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 7, padding: "10px 22px", background: "#3b82f6", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: "pointer" }}
                        >
                            <IcoPlus /> Add First Address
                        </button>
                    )}
                    {(search || activeFilter) && (
                        <button
                            onClick={() => { setSearch(""); setDebouncedSearch(""); setActiveFilter(""); }}
                            style={{ marginTop: 4, padding: "9px 20px", background: "#f1f5f9", color: "#374151", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
                        >
                            Clear Filters
                        </button>
                    )}
                </div>
            ) : (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14, animation: "ua-fadeUp .2s ease" }}>
                    {addresses.map((address) => (
                        <AddressCard
                            key={address.id}
                            address={address}
                            onView={(a) => setDetailAddress(a)}
                            onEdit={(a) => { setSelectedAddress(a); setFormMode("edit"); setShowForm(true); }}
                            onDelete={(a) => setDeleteTarget(a)}
                        />
                    ))}
                </div>
            )}

            {showForm && (
                <AddressFormModal
                    mode={formMode}
                    initialData={formMode === "edit" ? selectedAddress : null}
                    vendorProfileId={vendorProfileId}
                    onClose={() => { setShowForm(false); setSelectedAddress(null); }}
                    onSuccess={handleFormSuccess}
                />
            )}

            {detailAddress && (
                <AddressDetailModal
                    address={detailAddress}
                    onClose={() => setDetailAddress(null)}
                    onEdit={(a) => { setSelectedAddress(a); setFormMode("edit"); setShowForm(true); }}
                />
            )}

            {deleteTarget && (
                <ConfirmDeleteModal
                    address={deleteTarget}
                    loading={deleting}
                    onConfirm={handleDelete}
                    onCancel={() => setDeleteTarget(null)}
                />
            )}
        </div>
    );
};

export default UserAddress;